import React from "react";
import { Client } from "../../types";

interface ClientSummaryProps {
  client: Client;
}

const ClientSummary: React.FC<ClientSummaryProps> = ({ client }) => {
  return (
    <div className="mb-10 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      <div className="bg-primary-50 px-8 py-6 border-b border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900">
          Financial Summary
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Overview of all transactions with {client.name}
        </p>
      </div>
      <div className="p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-green-50 rounded-xl border border-green-100">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <div className="text-3xl font-bold text-green-600 mb-2">
              ₹
              {client.totalGiven.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}
            </div>
            <div className="text-sm font-medium text-green-700">
              Money Given
            </div>
            <div className="text-xs text-green-600 mt-1">Amount you lent</div>
          </div>

          <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-100">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            </div>
            <div className="text-3xl font-bold text-blue-600 mb-2">
              ₹
              {client.totalTaken.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}
            </div>
            <div className="text-sm font-medium text-blue-700">Money Taken</div>
            <div className="text-xs text-blue-600 mt-1">
              Amount you borrowed
            </div>
          </div>

          <div
            className={`text-center p-6 rounded-xl border ${client.effectiveBalance >= 0 ? "bg-emerald-50 border-emerald-100" : "bg-red-50 border-red-100"}`}
          >
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 ${client.effectiveBalance >= 0 ? "bg-emerald-500" : "bg-red-500"}`}
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div
              className={`text-3xl font-bold mb-2 ${client.effectiveBalance >= 0 ? "text-emerald-600" : "text-red-600"}`}
            >
              {client.effectiveBalance >= 0 ? "+" : "-"}₹
              {Math.abs(client.effectiveBalance).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}
            </div>
            <div
              className={`text-sm font-medium ${client.effectiveBalance >= 0 ? "text-emerald-700" : "text-red-700"}`}
            >
              Net Balance
            </div>
            <div
              className={`text-xs mt-1 ${client.effectiveBalance >= 0 ? "text-emerald-600" : "text-red-600"}`}
            >
              {client.effectiveBalance > 0
                ? "They owe you"
                : client.effectiveBalance < 0
                  ? "You owe them"
                  : "Settled"}
            </div>
          </div>

          <div className="text-center p-6 bg-gray-50 rounded-xl border border-gray-100">
            <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div
              className={`inline-flex px-4 py-2 rounded-full text-sm font-medium mb-2 ${client.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
            >
              {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
            </div>
            <div className="text-sm font-medium text-gray-700">Status</div>
            <div className="text-xs text-gray-600 mt-1">Account status</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientSummary;
